// src/components/recipient/RecipientFlow.tsx
import { useState, useEffect, useCallback, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { RecipientProposal } from "./RecipientProposal";
import { RecipientLetter } from "./RecipientLetter";
import { RecipientBouquet } from "./RecipientBouquet";
import { RecipientLoading } from "./RecipientLoading";
import { RecipientCompletion } from "./RecipientCompletion";
import { RecipientMusic } from "./RecipientMusic";
import { CardData } from "../../types/card.types";
import { ARCHETYPES, ArchetypeId } from "../../constants/archetypes";
import { cardService } from "../../services/card.service";
import { FlowStep, EmotionalTheme } from "../../types/recipient.types";
import { useResponsive } from "../../hooks/useResponsive";

interface RecipientFlowProps {
  card: CardData;
  onComplete?: () => void;
  isPreview?: boolean;
  nextIsPay?: boolean;
  shouldAutoAdvance?: boolean;
}

const FLOW_STEPS: FlowStep[] = ["loading", "proposal", "letter", "bouquet", "completed"];

// Map titles to archetypes
const TITLE_TO_ARCHETYPE: Record<string, ArchetypeId> = {
  "You Did It!": "moments",
  "To My Healing Heart": "safehaven",
  "To My Love": "heartbeat",
  // Add more mappings as needed
};

// Helper function to get archetype from card
const getArchetypeFromCard = (card: CardData): ArchetypeId => {
  // First check if archetype exists directly
  if (card.archetype && card.archetype in ARCHETYPES) {
    return card.archetype as ArchetypeId;
  }

  // If no archetype, try to map from title
  if (card.title && TITLE_TO_ARCHETYPE[card.title]) {
    return TITLE_TO_ARCHETYPE[card.title];
  }

  // Default fallback
  return "heartbeat";
};

export function RecipientFlow({
  card,
  onComplete,
  isPreview = false,
  nextIsPay = false,
  shouldAutoAdvance = false,
}: RecipientFlowProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState<FlowStep>("loading");
  const { isMobile } = useResponsive();

  // Get archetype dynamically
  const archetypeId = useMemo(() => getArchetypeFromCard(card), [card]);
  const archConfig = ARCHETYPES[archetypeId];

  // Create emotional theme
  const theme: EmotionalTheme = useMemo(
    () => ({
      primary: card.primaryColor || archConfig.defaultCardData.primaryColor,
      secondary: card.secondaryColor || archConfig.defaultCardData.secondaryColor,
      accent: card.accentColor || archConfig.defaultCardData.accentColor,
      ambient: `${card.primaryColor || archConfig.defaultCardData.primaryColor}33`,
    }),
    [card, archConfig]
  );

  const isArchetypePreviewRoute = useCallback((): boolean => {
    return location.pathname.startsWith("/preview/archetype/");
  }, [location.pathname]);

  const hasBouquet = useMemo(() => {
    return card.includeBouquet && (card.bouquetAmount ?? 0) > 0;
  }, [card.includeBouquet, card.bouquetAmount]);

  // Mark as opened when user reaches proposal
  useEffect(() => {
    if (currentStep === "proposal" && !isPreview && card.status === "paid" && !card.viewedAt) {
      cardService.markAsOpened(card.id).catch(console.error);
    }
  }, [currentStep, isPreview, card.id, card.status, card.viewedAt]);

  // Auto-advance for preview mode
  useEffect(() => {
    if (!isPreview || !shouldAutoAdvance) return;

    const timers: NodeJS.Timeout[] = [];
    const currentIndex = FLOW_STEPS.indexOf(currentStep);

    if (currentIndex < FLOW_STEPS.length - 1) {
      const timer = setTimeout(
        () => {
          const nextStep = FLOW_STEPS[currentIndex + 1];
          if (nextStep === "bouquet" && !hasBouquet) {
            setCurrentStep("completed");
          } else {
            setCurrentStep(nextStep);
          }
        },
        currentStep === "letter" ? 6000 : 4000
      );

      timers.push(timer);
    }

    return () => timers.forEach(clearTimeout);
  }, [currentStep, isPreview, shouldAutoAdvance, hasBouquet]);

  const handleProposalAccept = useCallback(() => {
    setCurrentStep("letter");
  }, []);

  const handleLetterOpen = useCallback(() => {
    setCurrentStep(hasBouquet ? "bouquet" : "completed");
    if (!hasBouquet && onComplete) onComplete();
  }, [hasBouquet, onComplete]);

  const handleBouquetReceive = useCallback(() => {
    setCurrentStep("completed");
    if (onComplete) onComplete();
  }, [onComplete]);

  const handleCreateOwn = useCallback(
    (arch?: string, cardId?: string) => {
      isPreview
        ? navigate(`/payment/${cardId}`)
        : isPreview
          ? navigate(`/create?archetype=${arch}`)
          : navigate("/archetypes");
    },
    [navigate, isPreview]
  );

  const handleReplay = useCallback(() => {
    setCurrentStep("loading");
  }, []);

  console.log("archConfig: ", archConfig);

  return (
    <div className="h-screen w-screen overflow-hidden bg-black">
      <RecipientMusic archetype={archetypeId} currentStep={currentStep} isMuted={isMobile} />

      <AnimatePresence mode="wait">
        {currentStep === "loading" && (
          <RecipientLoading
            key="loading"
            config={{
              style: archConfig.loadingScreen.style,
              message: archConfig.loadingScreen.message.replace("{name}", card.recipientName),
              progressMessages: archConfig.loadingScreen.progressMessages,
              icon: archConfig.loadingScreen.icon,
              backgroundColor: archConfig.loadingScreen.backgroundColor,
              particleCount: archConfig.loadingScreen.particleCount,
              archetype: archetypeId,
              theme,
            }}
            onComplete={() => setCurrentStep("proposal")}
          />
        )}

        {currentStep === "proposal" && archConfig.proposal?.enabled && (
          <RecipientProposal
            key="proposal"
            config={{
              enabled: archConfig.proposal?.enabled ?? true,
              question:
                archConfig.proposal?.question.replace("{name}", card.recipientName) ||
                `${card.recipientName}, are you ready for something special?`,
              image: isArchetypePreviewRoute()
                ? archConfig.defaultCardData.proposalBgImage
                : (card.recipientImage ?? undefined),
              yesButtonText: archConfig.proposal?.yesButtonText || "Open This Moment",
              noButtonTexts: archConfig.proposal?.noButtonTexts || [],
              floatingHearts: archConfig.proposal?.floatingHearts,
              confetti: archConfig.proposal?.confetti,
              primaryColor: theme.primary,
              secondaryColor: theme.secondary,
              archetype: archetypeId,
              theme,
              delayBeforeButtons: 3000,
            }}
            senderName={card.senderName}
            recipientName={card.recipientName}
            onAccept={handleProposalAccept}
          />
        )}

        {currentStep === "letter" && (
          <RecipientLetter
            key="letter"
            config={{
              title: card.title || "To You",
              content: card.message || "",
              signature: card.senderName,
              style: archConfig.letter.style,
              hasSecretMessage: !!card.personalNote,
              secretMessage: card.personalNote,
              paperTexture: archConfig.letter.paperTexture,
              seal: archConfig.letter.seal,
              accentColor: theme.accent,
              primaryColor: theme.primary,
              secondaryColor: theme.secondary,
              archetypeName: archConfig.name,
              bgImage: archConfig.defaultCardData.letterBgImage,
              hasBouquet,
              image: isArchetypePreviewRoute()
                ? archConfig.defaultCardData.proposalBgImage
                : (card.recipientImage ?? undefined),
              theme,
            }}
            senderName={card.senderName}
            recipientName={card.recipientName}
            onOpen={handleLetterOpen}
          />
        )}

        {currentStep === "bouquet" && hasBouquet && (
          <RecipientBouquet
            key="bouquet"
            config={{
              enabled: hasBouquet,
              totalValue: card.bouquetAmount || 0,
              petals: card.petals || 5,
              interactive: true,
              receiveButtonText: archConfig.bouquet.receiveButtonText || "Receive Your Gift",
              showValue: archConfig.bouquet.showValue || true,
              message: `A digital bouquet worth KES ${(card.bouquetAmount || 0).toLocaleString()}`,
              primaryColor: theme.primary,
              secondaryColor: theme.secondary,
              theme,
            }}
            senderName={card.senderName}
            recipientName={card.recipientName}
            onReceive={handleBouquetReceive}
          />
        )}

        {currentStep === "completed" && (
          <RecipientCompletion
            key="completed"
            config={{
              hasBouquet,
              isPreview,
              nextIsPay,
              primaryColor: theme.primary,
              secondaryColor: theme.secondary,
              recipientName: card.recipientName,
              senderName: card.senderName,
              cardId: card.id,
              archetype: archetypeId,
              theme,
            }}
            onCreateOwn={() => handleCreateOwn(archetypeId, card.id)}
            onReplay={handleReplay}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
